import { FunctionComponent, useRef } from "react"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router-dom"

import OutlineBtn from "@fuse/sharedComponents/buttons/OutlineBtn"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import ContainerElements from "@fuse/sharedComponents/Containerelements"
import Bottom from "@fuse/sharedComponents/legacyUI/Bottom"
import GradientBannerContainer from "@fuse/sharedComponents/legacyUI/GradientBannerContainer"
import { LetsHangDownloadHeader } from "@fuse/sharedComponents/legacyUI/Top/DownloadButton"
import {
  greyColor,
  headingStyle,
} from "@fuse/sharedComponents/legacyUI/Top/styles"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { createGroupsType, EmptyObject } from "@letshang/constants"
import { get, isEmpty } from "lodash-es"

import {
  DownloadLinkMap,
  onOpenDownloadLink,
  useDeviceDetection,
} from "@fuse/utils/DeviceDection"

import { Colors } from "@fuse/styles/color"

import LocationPin from "@fuse/assets/icons/legacy/icon-location-pin.svg"
import UserAvatar from "@fuse/assets/icons/legacy/icon-user-avatar.svg"
import UserCard from "@fuse/assets/icons/legacy/icon-user-card.svg"
import First from "@fuse/assets/images/img-event-first.svg"
import GroupPlaceHolder from "@fuse/assets/images/legacy/img-group-placeholder.svg"

import { Content } from "./constant"
import { useGroups } from "./controller"
import EmptyGroup from "./emptyGroup"
import GroupLanding from "./groupLanding"
import "./style.css"

const EventsLandingPageTop = ({
  groupDocData,
  onGroupDetails,
}: {
  groupDocData: createGroupsType | EmptyObject
  onGroupDetails: () => void
}) => {
  return (
    <div className="flex items-center justify-center">
      <div className="md:mt-[78] mt-[50px] max-w-screen-xl mx-[30px] md:mx-[50px] lg:mx-[100px]">
        <LetsHangDownloadHeader />
        <div className="md:flex flex-1">
          <div className="flex-1 justify-between text-start mb-[73px] md:mb-[80px]">
            <FancyText
              style={headingStyle}
              className="text-[38px] md:text-[38px] lg:text-[48px] xl:text-[58px] space-[-0.658px] tracking-[-0.658px] "
            >
              Join the {get(groupDocData, "groupName", "-")} Group!
            </FancyText>
            <div className="my-[26px] md:my-[40px]">
              <FancyText
                className="text-[15px] md:text-[18px] mb-[10px] md:mb-[10px]"
                style={greyColor}
              >
                Hi there 👋
              </FancyText>
              <FancyText
                className="text-[15px] md:text-[18px] mb-[10px] md:mb-[10px]"
                style={greyColor}
              >
                You have been invited to join the{" "}
                <b>“{get(groupDocData, "groupName", "-")}”</b> group.
              </FancyText>
              <FancyText
                className="text-[15px] md:text-[18px] mb-[10px] md:mb-[10px]"
                style={greyColor}
              >
                Connect with like-minded individuals to share weekly activities.
              </FancyText>
              <FancyText
                className="text-[15px] md:text-[18px] mb-[10px] md:mb-[10px]"
                style={greyColor}
              >
                Thank you for your interest in joining this group!
              </FancyText>
            </div>
            <div className="flex flex-row mb-[40px]">
              <OutlineBtn
                label={"View Details"}
                style={{ borderColor: Colors.primary }}
                className="mr-4 px-6 lg:px-8"
                labelClassName={
                  "text-[12px] md:text-[14px] lg:text-[15px] text-primary"
                }
                onClick={onGroupDetails}
              />
            </div>
          </div>
          <div className="flex-1 lg:flex items-center justify-center">
            <img src={First} alt="First" className="hero-image" />
          </div>
        </div>
      </div>
    </div>
  )
}

const EventBanner = () => {
  return (
    <GradientBannerContainer
      className={"xl:px-52 lg:px-36 md:px-5 sm:px-5 px-0 mx-auto"}
    >
      <div className="flex-1 justify-between mx-[20px]">
        <FancyText className="text-[38px] md:text-[38px] lg:text-[48px] xl:text-[58px] space-[-0.658px] tracking-[-0.658px] text-white">
          <b>The Group Journal for Your Social Circle 🌟</b>
        </FancyText>
        <div className="my-[26px] md:my-[40px]">
          <FancyText className="text-[15px] md:text-[18px] text-white">
            Online group experiences go beyond polished social feeds. Today,
            people seek genuine connections and real-life group activities.
          </FancyText>
          <FancyText className="text-[15px] md:text-[18px] text-white">
            That's why we've created a platform to share moments, make memories,
            and connect through group events.
          </FancyText>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {Content.map((content, index) => (
          <ContainerElements
            key={index.toString()}
            img={content.img}
            heading={content.heading}
            content={content.content}
          />
        ))}
      </div>
    </GradientBannerContainer>
  )
}

const EventInvitation = ({
  groupDocData,
}: {
  groupDocData: createGroupsType | EmptyObject
}) => {
  const device = useDeviceDetection()
  const downloadLink = DownloadLinkMap[device]

  return (
    <div className="flex justify-center md:mt-[74px] mt-[50px] p-4 md:mb-[74px] mb-[50px] mx-[20px] md:mx-[50px] lg:mx-[100px] xl:mx-[200px] overflow-hidden">
      <div className="flex-1 max-w-screen-lg">
        <FancyText className="text-[15px] md:text-[26px] mb-[10px]">
          <b>Your Group Invitation</b>
        </FancyText>
        <FancyText
          style={greyColor}
          className="text-[12px] md:text-[18px] mb-[26px] md:mb-[40px]"
        >
          Connect, Celebrate, Cherish. Let's Hang!
        </FancyText>

        <div className="shadow-lg flex rounded-xl md:p-8 lg:p-12 flex-col lg:flex-row flex-wrap">
          <div className="flex-1 items-center justify-center">
            <div className="flex items-center justify-center">
              <img
                src={GroupPlaceHolder}
                className="max-w-xs md:max-w-sm p-8 lg:p-0"
                alt="First"
              />
            </div>
          </div>
          <div className="flex-1">
            <div className="p-5">
              <h5 className="text-2xl font-bold text-start text-black font-dm">
                {get(groupDocData, "groupName", "-")}
              </h5>

              <div className="my-6">
                <div className="flex flex-row">
                  <img
                    src={UserCard}
                    className="max-w-xs md:max-w-sm"
                    alt="First"
                  />
                  <div className="flex-col ml-3">
                    <h5 className="text-start font-dm">
                      {get(groupDocData, "initiatorName", "-")} is the group
                      admin
                    </h5>
                  </div>
                </div>

                <div className="flex flex-row my-3">
                  <img
                    src={LocationPin}
                    className="max-w-xs md:max-w-sm"
                    alt="First"
                  />
                  <div className="flex-col ml-3">
                    <h5 className="text-start font-dm">
                      {get(groupDocData, "groupLocation.locality", "-")}
                    </h5>
                  </div>
                </div>

                <div className="flex flex-row">
                  <img
                    src={UserAvatar}
                    className="max-w-xs md:max-w-sm"
                    alt="First"
                  />
                  <div className="flex-col ml-3">
                    <h5 className="text-start font-dm">
                      {get(groupDocData, "totalMember", 0)} members
                    </h5>
                  </div>
                </div>
              </div>

              <div>
                <h6 className="text-xl font-bold text-start font-dm">
                  About this group
                </h6>
                <h6 className="text-sm text-start font-dm">
                  {get(groupDocData, "groupDescription", 0)}
                </h6>
              </div>

              <div className="flex mt-6">
                <SolidBtn
                  className="px-6 lg:px-10"
                  onClick={() => onOpenDownloadLink(downloadLink)}
                >
                  <p className="text-[12px] md:text-[14px]">Join now</p>
                </SolidBtn>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export const GroupLandingPage: FunctionComponent = () => {
  const groupInvitationRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const params = useParams()
  const groupId = get(params, "*", "")
  const { isLoading, groupDetails } = useGroups(groupId)
  const groupDocData: createGroupsType | EmptyObject = get(
    groupDetails,
    "groupDocData",
    {},
  )

  const isGroupExist = isEmpty(groupDocData)

  const onGroupDetails = () => {
    if (groupInvitationRef && groupInvitationRef.current) {
      groupInvitationRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }
  }

  const onHomePage = () => {
    navigate("/")
  }

  if (groupId && !isLoading) {
    return (
      <div className="flex flex-col">
        {groupId && isLoading && <LoadingSpinner />}

        {isGroupExist && <EmptyGroup onHomePage={onHomePage} />}

        {!isGroupExist && (
          <>
            <EventsLandingPageTop
              groupDocData={groupDocData}
              onGroupDetails={onGroupDetails}
            />

            <EventBanner />
            <div ref={groupInvitationRef}>
              <EventInvitation groupDocData={groupDocData} />
            </div>
            <Bottom />
          </>
        )}
      </div>
    )
  } else {
    return (
      <div>
        {isLoading && <LoadingSpinner />}

        <GroupLanding />
      </div>
    )
  }
}
